"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useLayoutEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { PropertyTable } from "@/components/ui/SearchTable/components/PropertyTable";
import { formatTitleByLocale } from "@/data/helper/formatTitleByLocale";
import { getRedirectUrl } from "@/data/helper/getRedirectUrl";
import { titleToSlug } from "@/data/helper/titleToSlug";
import { DealType } from "@/enums/dealType";
import { FeatureFlags } from "@/enums/feature-flags";
import { EnumViewMode } from "@/enums/view-mode";
import { useUnifiedListing } from "@/hooks/useUnifiedListing";
import { useViewMode } from "@/providers/ViewContext";
import { propertyService } from "@/services/property.service";
import { SearchService } from "@/services/search.service";
import { DevelopersApiResponse } from "@/types/property.types";
import { EmptyState } from "@/ui/Common/EmptyState";
import Loader from "@/ui/Common/Loader";
import { ObjectCard } from "@/ui/Common/ObjectCard";
import { ObjectList } from "@/ui/Common/ObjectList";
import { PaginationContainer } from "@/ui/Common/Pagination";
import { EnhancedSearchPanel } from "@/ui/EnhancedSearchPanel";
import { SearchPanel } from "@/ui/SearchPanel";
import { SearchResultHeader } from "@/ui/SearchResultHeader";
import { restoreScroll } from "@/utils/scrollRestoration";
import { useUserRole } from "@/hooks/useUserRole";

// Match server-side PAGE_SIZE constant
const PAGE_SIZE = parseInt(process.env.NEXT_PUBLIC_PAGE_SIZE || "12", 10);

interface Props {
  values: DevelopersApiResponse;
  h1?: string;
}

export default function BuyPage({ values, h1 }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations();
  const role = useUserRole();

  // Use the unified listing hook with BUY deal type
  const {
    data,
    transformedData,
    isFetching,
    isInitializing,
    currentPage,
    limit,
    total,
    totalPage,
    setPage,
    setPageSize,
    isEmpty,
    viewMode,
    isRealtor,
    initialFilters,
    localizedLocationString,
  } = useUnifiedListing({
    dealType: DealType.BUY,
    basePath: "/buy",
    values,
  });

  const isLoading = isFetching || !data || isInitializing;

  useLayoutEffect(() => {
    restoreScroll(DealType.BUY);
  }, []);

  return (
    <section className="min-h-screen">
      <EnhancedSearchPanel
        header={h1 || t("Sale")}
        dealType={DealType.BUY}
        initialFilters={initialFilters}
        localizedLocationString={isLoading ? " " : localizedLocationString}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SearchResultHeader
            total={total}
            dealType={DealType.BUY}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />

          <Feature name={FeatureFlags.PROPERTIES_LIST_TABLE_VIEW}>
            <Feature.Enabled>
              {viewMode === EnumViewMode.TABLE && isRealtor ? (
                <PropertyTable
                  properties={transformedData}
                  loading={isFetching}
                  currentSort="date_desc"
                  onSortChange={(sort) => {
                    // Handle sort change
                  }}
                  pagination={{
                    currentPage,
                    pageSize: limit,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: setPageSize,
                  }}
                  enableBulkActions={true}
                  hideInternalPagination={true}
                />
              ) : (
                <ObjectList isEmpty={isEmpty}>
                  <ObjectList.Items viewMode={viewMode}>
                    {transformedData.map((item) => (
                      <ObjectCard
                        key={item.id ?? item.title}
                        id={item.id}
                        title={item.title ?? "N/A"}
                        location={item.location ?? item.city ?? "N/A"}
                        slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                        view={viewMode}
                        translations={item.translations}
                        canArchive={role.isRealtor}
                        {...item}
                      />
                    ))}
                  </ObjectList.Items>
                  <ObjectList.Empty>
                    <EmptyState />
                  </ObjectList.Empty>
                </ObjectList>
              )}
            </Feature.Enabled>
            <Feature.Disabled>
              <ObjectList isEmpty={isEmpty}>
                <ObjectList.Items viewMode={viewMode}>
                  {transformedData.map((item) => (
                    <ObjectCard
                      key={item.id ?? item.title}
                      id={item.id}
                      title={item.title ?? "N/A"}
                      location={item.location ?? item.city ?? "N/A"}
                      slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                      view={viewMode}
                      translations={item.translations}
                      canArchive={role.isRealtor}
                      {...item}
                    />
                  ))}
                </ObjectList.Items>
                <ObjectList.Empty>
                  <EmptyState />
                </ObjectList.Empty>
              </ObjectList>
            </Feature.Disabled>
          </Feature>

          {/* External pagination: always when multiple pages */}
          {totalPage > 1 && (
            <PaginationContainer
              totalPage={totalPage}
              currentPage={currentPage}
              pathname={pathname}
              searchParams={searchParams}
            />
          )}
        </>
      )}
    </section>
  );
}

// ORIGINAL VERSION: Pre-realtor implementation (restored from git history)
function BuyPageOriginal({ values, h1 }: Props) {
  const t = useTranslations();
  const role = useUserRole();
  const { viewMode } = useViewMode();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const searchParams = useSearchParams();
  const hasSearchParams = searchParams.toString().length > 0;

  // Get current page from path: /buy/page/3/ -> 3, /buy -> 1
  const getCurrentPageFromPath = (): number => {
    // Match /page/N with or without trailing slash
    const match = pathname.match(/\/page\/(\d+)\/?$/);
    return match ? parseInt(match[1], 10) : 1;
  };

  const urlCurrentPage = getCurrentPageFromPath();
  const queryPage = Number(searchParams.get("page"));
  const normalizedQueryPage =
    Number.isFinite(queryPage) && queryPage > 0 ? queryPage : undefined;

  // For path-based pagination, always use URL page when it's greater than 1
  // For base pages without pagination, use server data or query page fallback
  const serverPage = values?.page || 1;
  const currentPage =
    normalizedQueryPage ?? (urlCurrentPage > 1 ? urlCurrentPage : serverPage);
  const page = currentPage;

  const { data, isFetching, refetch } = useQuery({
    queryKey: hasSearchParams
      ? ["searchBuy", searchParams, currentPage]
      : ["defaultBuy", currentPage],
    queryFn: async () => {
      if (hasSearchParams) {
        const result = await SearchService.getAllProperty(searchParams);
        // Adapt PropertySearchResponse to DevelopersApiResponse structure if needed
        // For now, casting as any to avoid type mismatch errors since the component handles missing data
        return result as any as DevelopersApiResponse;
      }
      return propertyService.fetchAllProperty({
        category: DealType.BUY,
        page: currentPage,
        limit: PAGE_SIZE,
      });
    },
    initialData:
      !hasSearchParams && values?.page === currentPage && currentPage === 1
        ? values
        : undefined,
    placeholderData: !hasSearchParams && currentPage === 1 ? values : undefined,
  });

  useEffect(() => {
    // Always refetch when page changes (except for page 1 without search params)
    if (hasSearchParams || currentPage > 1) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, hasSearchParams, currentPage, pathname]);

  const total = useMemo(() => {
    return data?.total || 0;
  }, [data]);

  const totalPage = useMemo(() => {
    const calculatedLimit = data?.limit || PAGE_SIZE; // Use same default as server
    return Math.ceil((data?.total || 0) / calculatedLimit);
  }, [data]);

  // Transform data to apply locale-specific title and description formatting
  const transformedData = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    return data.data.map((item: any) => ({
      ...item,
      redirectLink:
        item.redirectLink || getRedirectUrl(item.dealType || DealType.BUY),
      title: formatTitleByLocale({
        title: item.title || "",
        translations: item.translations || [],
        locale,
        key: "title",
      }),
      description: formatTitleByLocale({
        title: item.description || "",
        translations: item.translations || [],
        locale,
        key: "title",
      }),
    }));
  }, [data, locale]);

  // Check if data is empty for empty state handling
  const isEmpty = !data?.data || data.data.length === 0;

  if (isFetching) {
    return <Loader />;
  }

  return (
    <section>
      {data && (
        <>
          <SearchPanel
            dealType={DealType.BUY}
            header={h1 || t("Sale")}
          />
          <SearchResultHeader
            total={total}
            dealType={DealType.BUY}
            pageSize={data?.limit || PAGE_SIZE}
          />
          <ObjectList isEmpty={isEmpty}>
            <ObjectList.Items viewMode={viewMode}>
              {(transformedData as [])?.map((item: any) => (
                <ObjectCard
                  key={item.id ?? item.title}
                  id={item.id}
                  title={item.title ?? "N/A"}
                  location={item.location ?? item.city ?? "N/A"}
                  slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                  view={viewMode}
                  translations={item.translations}
                  canArchive={role.isRealtor}
                  {...item}
                />
              ))}
            </ObjectList.Items>
            <ObjectList.Empty>
              <EmptyState />
            </ObjectList.Empty>
          </ObjectList>
          {totalPage > 1 && (
            <PaginationContainer
              totalPage={totalPage}
              currentPage={currentPage}
              pathname={pathname}
              searchParams={searchParams}
              paginationType={hasSearchParams ? "query" : "path"}
            />
          )}
        </>
      )}
    </section>
  );
}
